import { StripeConnectedAccount } from '@umss/shared/data-access';
import { humanizeFields } from './stripe-requirements.helper';

export type StatusSeverity =
  | 'success'
  | 'info'
  | 'info-warning'
  | 'warning'
  | 'error'
  | 'action-required';

export type StatusAction =
  | 'NONE'
  | 'WAIT'
  | 'CREATE_ACCOUNT'
  | 'ADD_BANK_ACCOUNT'
  | 'OPEN_STRIPE_ONBOARDING'
  | 'CONTACT_SUPPORT';

export interface StatusInfo {
  severity: StatusSeverity;
  title: string;
  message: string;
  action: StatusAction;
  actionLabel?: string;
  dismissable?: boolean;
}

export function getStatusInfo(data: StripeConnectedAccount | null): StatusInfo {
  if (!data || !data.hasAccount) {
    return {
      severity: 'action-required',
      title: 'Set Up Your Payment Account',
      message: 'You need to create a payment account to receive earnings.',
      action: 'CREATE_ACCOUNT'
    };
  }

  if (data.status === 'Disabled') {
    return {
      severity: 'error',
      title: 'Payment Account Disabled',
      message:
        'Your payment account has been permanently disabled. Please contact support for assistance.',
      action: 'CONTACT_SUPPORT'
    };
  }

  if (data.status === 'Restricted') {
    const overdueFields =
      data.pastDue.length > 0 ? data.pastDue : data.currentlyDue;

    if (overdueFields.every((f) => f === 'external_account')) {
      return {
        severity: 'error',
        title: 'Payouts Suspended — Bank Account Required',
        message:
          'Your payouts are suspended because no bank account is linked. Please add your bank account in the section below to restore your payouts.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    const fields = humanizeFields(overdueFields);
    return {
      severity: 'error',
      title: 'Payouts Suspended',
      message: `Your payouts are currently suspended. The following information is overdue: ${fields}. Please complete verification to restore your account.`,
      action: 'OPEN_STRIPE_ONBOARDING'
    };
  }

  if (data.status === 'RequiresInformation') {
    const isPastDue = data.pastDue.length > 0;
    const overdueFields = isPastDue ? data.pastDue : data.currentlyDue;

    if (overdueFields.every((f) => f === 'external_account')) {
      return {
        severity: isPastDue ? 'error' : 'warning',
        title: 'Bank Account Required',
        message:
          'A bank account is required to receive payouts. Please link your bank account in the section below.',
        action: 'ADD_BANK_ACCOUNT'
      };
    }

    const fields = humanizeFields(overdueFields);
    return {
      severity: isPastDue ? 'error' : 'warning',
      title: isPastDue
        ? 'Action Required — Overdue Verification'
        : 'Additional Information Needed',
      message: isPastDue
        ? `The following information is overdue: ${fields}. Your payouts may be suspended if not provided soon.`
        : `Stripe requires additional information to keep your account active: ${fields}. Please complete verification.`,
      action: 'OPEN_STRIPE_ONBOARDING'
    };
  }

  if (data.status === 'Pending') {
    if (data.currentlyDue.length > 0) {
      const fields = humanizeFields(data.currentlyDue);
      return {
        severity: 'warning',
        title: 'Complete Your Account Setup',
        message: `Your account is being set up. The following is still needed: ${fields}.`,
        action: data.currentlyDue.includes('external_account')
          ? 'ADD_BANK_ACCOUNT'
          : 'OPEN_STRIPE_ONBOARDING'
      };
    }
    return {
      severity: 'info',
      title: 'Account Under Review',
      message:
        "Your payment account is being reviewed by Stripe. This usually takes a few minutes. We'll update you when it's ready.",
      action: 'WAIT'
    };
  }

  if (data.status === 'Verified' && data.onboardingCompleted) {
    if (data.eventuallyDue.length > 0) {
      const fields = humanizeFields(data.eventuallyDue);
      return {
        severity: 'info-warning',
        title: 'Payment Account Active',
        message: `Your account is active and you can receive payments. However, Stripe will eventually require: ${fields}. We recommend completing this now to avoid future interruptions.`,
        action: 'OPEN_STRIPE_ONBOARDING',
        actionLabel: 'Complete Verification',
        dismissable: true
      };
    }
    return {
      severity: 'success',
      title: 'Payment Account Ready',
      message:
        "Your payment account is fully set up. You're all set to receive earnings!",
      action: 'NONE'
    };
  }

  // Fallback for UnderReview or any other edge case
  return {
    severity: 'info',
    title: 'Account Under Review',
    message:
      "Your payment account is being reviewed. We'll update you when it's ready.",
    action: 'WAIT'
  };
}
